'use client';

import { useEffect, useRef, useState } from 'react';
import { BookOpen, CircleCheck, FileText, Heart, House, Lightbulb, Palette, Smile, Users } from 'lucide-react';
import {
  IGROW_AREA_NAMES,
  IGROW_COMMUNICATION_OPTIONS,
  saveIgrowToAppClust,
  type IgrowRegistrationPayload,
} from '@/lib/igrowRegistration';
import AreaCard from './components/AreaCard';
import DecorativeBlobs from './components/DecorativeBlobs';
import FormField from './components/FormField';
import SectionCard from './components/SectionCard';
import styles from './igrow.module.css';
import {
  FIELD_ORDER,
  validateIgrowForm,
  type IgrowFieldErrors,
  type IgrowFormValues,
} from './validation';

const AREA_CARDS = [
  {
    name: IGROW_AREA_NAMES[0],
    subtitle: 'Self-help and independence',
    background: '#DFF1FF',
    accent: '#4E93F0',
    icon: House,
  },
  {
    name: IGROW_AREA_NAMES[1],
    subtitle: 'Building connections and confidence',
    background: '#E7F9E1',
    accent: '#3EAA5C',
    icon: Users,
  },
  {
    name: IGROW_AREA_NAMES[2],
    subtitle: 'Practical learning for everyday life',
    background: '#EBE6FD',
    accent: '#7A52C4',
    icon: BookOpen,
  },
  {
    name: IGROW_AREA_NAMES[3],
    subtitle: 'Exploring creativity and expression',
    background: '#FDE0F2',
    accent: '#E85AAD',
    icon: Palette,
  },
  {
    name: IGROW_AREA_NAMES[4],
    subtitle: 'Building future opportunities',
    background: '#FFF4CE',
    accent: '#F0B429',
    icon: Lightbulb,
  },
] as const;

const INITIAL_VALUES: IgrowFormValues = {
  parentName: '',
  parentPhone: '',
  parentEmail: '',
  childName: '',
  childAge: '',
  childSchool: '',
  communicationPreference: '',
  loves: '',
  activities: '',
  goals: '',
  consentContact: false,
  consentAccuracy: false,
  areas: [],
};

export default function IgrowRegistrationForm() {
  const [values, setValues] = useState<IgrowFormValues>(INITIAL_VALUES);
  const [errors, setErrors] = useState<IgrowFieldErrors>({});
  const [submitCount, setSubmitCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const submittingRef = useRef(false);

  useEffect(() => {
    if (submitCount === 0) return;
    const firstInvalid = FIELD_ORDER.find((field) => errors[field]);
    if (!firstInvalid) return;
    const element = document.getElementById(firstInvalid);
    if (!element) return;
    element.focus();
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [submitCount, errors]);

  function updateField<K extends keyof IgrowFormValues>(field: K, value: IgrowFormValues[K]) {
    setValues((current) => ({ ...current, [field]: value }));
    if (field in errors) {
      setErrors((current) => {
        const next = { ...current };
        delete next[field as keyof IgrowFieldErrors];
        return next;
      });
    }
  }

  function toggleArea(name: string) {
    setValues((current) => ({
      ...current,
      areas: current.areas.includes(name)
        ? current.areas.filter((area) => area !== name)
        : [...current.areas, name],
    }));
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submittingRef.current) return;

    const nextErrors = validateIgrowForm(values);
    setErrors(nextErrors);
    setSubmitError('');

    if (Object.keys(nextErrors).length > 0) {
      setSubmitCount((count) => count + 1);
      return;
    }

    const payload: IgrowRegistrationPayload = {
      parent: {
        name: values.parentName.trim(),
        phone: values.parentPhone.trim(),
        email: values.parentEmail.trim(),
      },
      child: {
        name: values.childName.trim(),
        age: Number(values.childAge),
        school: values.childSchool.trim(),
        communicationPreference: values.communicationPreference as IgrowRegistrationPayload['child']['communicationPreference'],
      },
      interests: {
        loves: values.loves.trim(),
        activities: values.activities.trim(),
        goals: values.goals.trim(),
      },
      areas: IGROW_AREA_NAMES.filter((name) => values.areas.includes(name)),
      consent: {
        contact: values.consentContact,
        accuracy: values.consentAccuracy,
      },
      submittedAt: new Date().toISOString(),
    };

    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      await saveIgrowToAppClust(payload);

      setIsSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setSubmitError("We couldn't submit your registration just now. Please try again in a moment.");
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }

  return (
    <main className={styles.page}>
      <DecorativeBlobs />
      <div className={styles.shell}>
        <header className={styles.header}>
          <div className={styles.logoFrame}>
            <img
              className={styles.logo}
              src="/registrations/igrow-logo.png"
              alt="I-GROW by The Speech Clinic Dubai"
              width={435}
              height={245}
            />
          </div>
          <div className={styles.heading}>
            <h1 className={styles.title}>I-GROW</h1>
            <p className={styles.subtitle}>Programme Registration</p>
            <p className={styles.tagline}>
              <span className={styles.discover}>Discover</span>
              <span className={styles.dot} aria-hidden="true">•</span>
              <span className={styles.develop}>Develop</span>
              <span className={styles.dot} aria-hidden="true">•</span>
              <span className={styles.explore}>Explore</span>
            </p>
          </div>
        </header>

        {isSubmitted ? (
          <section className={styles.success} aria-live="polite">
            <span className={styles.successBadge}>
              <CircleCheck size={28} strokeWidth={2.25} aria-hidden="true" />
            </span>
            <h2 className={styles.successTitle}>Thank you for registering!</h2>
            <p className={styles.successText}>
              We have received your interest in the I-GROW programme. The Speech Clinic Dubai will contact you with more information about upcoming batches, activities and opportunities.
            </p>
          </section>
        ) : (
          <>
            <section className={styles.welcome}>
              <h2 className={styles.welcomeTitle}>Welcome to I-GROW!</h2>
              <p className={styles.welcomeText}>
                A training and enrichment programme by <strong>The Speech Clinic Dubai</strong> for children and adolescents, mainly aged 8 years and above.
              </p>
              <p className={styles.welcomeText}>
                Please complete this form to register your interest in the I-GROW programme. We will contact you with more information about upcoming batches, activities and opportunities.
              </p>
            </section>

            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              <SectionCard title="1. Parent / Guardian Information" tone="lavender" icon={Users}>
                <div className={styles.grid3}>
                  <FormField id="parentName" label="Full Name" required error={errors.parentName}>
                    {(props) => (
                      <input
                        {...props}
                        name="parentName"
                        type="text"
                        autoComplete="name"
                        placeholder="Enter your full name"
                        value={values.parentName}
                        onChange={(event) => updateField('parentName', event.target.value)}
                      />
                    )}
                  </FormField>
                  <FormField id="parentPhone" label="Contact Number" required error={errors.parentPhone}>
                    {(props) => (
                      <input
                        {...props}
                        name="parentPhone"
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        placeholder="Enter contact number"
                        value={values.parentPhone}
                        onChange={(event) => updateField('parentPhone', event.target.value)}
                      />
                    )}
                  </FormField>
                  <FormField id="parentEmail" label="Email Address" required error={errors.parentEmail}>
                    {(props) => (
                      <input
                        {...props}
                        name="parentEmail"
                        type="email"
                        autoComplete="email"
                        placeholder="Enter email address"
                        value={values.parentEmail}
                        onChange={(event) => updateField('parentEmail', event.target.value)}
                      />
                    )}
                  </FormField>
                </div>
              </SectionCard>

              <SectionCard title="2. Child Information" tone="pink" icon={Smile}>
                <div className={`${styles.grid4} ${styles.childGrid}`}>
                  <FormField id="childName" label="Child's Full Name" required error={errors.childName}>
                    {(props) => (
                      <input
                        {...props}
                        name="childName"
                        type="text"
                        autoComplete="name"
                        placeholder="Enter child's name"
                        value={values.childName}
                        onChange={(event) => updateField('childName', event.target.value)}
                      />
                    )}
                  </FormField>
                  <FormField id="childAge" label="Child's Age" required error={errors.childAge}>
                    {(props) => (
                      <input
                        {...props}
                        name="childAge"
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={25}
                        placeholder="Enter age"
                        value={values.childAge}
                        onChange={(event) => updateField('childAge', event.target.value)}
                      />
                    )}
                  </FormField>
                  <FormField id="childSchool" label="Current School" optional>
                    {(props) => (
                      <input
                        {...props}
                        name="childSchool"
                        type="text"
                        placeholder="Enter school name"
                        value={values.childSchool}
                        onChange={(event) => updateField('childSchool', event.target.value)}
                      />
                    )}
                  </FormField>
                  <FormField id="communicationPreference" label="Communication Preference" optional variant="select">
                    {(props) => (
                      <select
                        {...props}
                        name="communicationPreference"
                        className={`${props.className} ${values.communicationPreference ? '' : styles.selectEmpty}`}
                        value={values.communicationPreference}
                        onChange={(event) => updateField('communicationPreference', event.target.value)}
                      >
                        <option value="">Select option</option>
                        {IGROW_COMMUNICATION_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    )}
                  </FormField>
                </div>
              </SectionCard>

              <SectionCard title="3. Interests & Skills" tone="lavender" icon={Heart}>
                <div className={`${styles.grid3} ${styles.interests}`}>
                  <FormField id="loves" label="What does your child love?" required error={errors.loves} variant="textarea">
                    {(props) => (
                      <textarea
                        {...props}
                        name="loves"
                        placeholder="Example: art, cooking, music, books, plants, technology, helping others etc."
                        value={values.loves}
                        onChange={(event) => updateField('loves', event.target.value)}
                      />
                    )}
                  </FormField>
                  <FormField id="activities" label="What activities does your child enjoy?" variant="textarea">
                    {(props) => (
                      <textarea
                        {...props}
                        name="activities"
                        placeholder="Enter activities"
                        value={values.activities}
                        onChange={(event) => updateField('activities', event.target.value)}
                      />
                    )}
                  </FormField>
                  <FormField
                    id="goals"
                    label="Any specific goals or areas you would like to focus on?"
                    optional
                    variant="textarea"
                  >
                    {(props) => (
                      <textarea
                        {...props}
                        name="goals"
                        placeholder="Enter your goals"
                        value={values.goals}
                        onChange={(event) => updateField('goals', event.target.value)}
                      />
                    )}
                  </FormField>
                </div>
              </SectionCard>

              <SectionCard title="4. I-GROW Areas of Interest" tone="pink" icon={CircleCheck}>
                <p className={styles.intro}>Please select the areas you are most interested in:</p>
                <div className={styles.areas}>
                  {AREA_CARDS.map((area) => (
                    <AreaCard
                      key={area.name}
                      name={area.name}
                      subtitle={area.subtitle}
                      background={area.background}
                      accent={area.accent}
                      icon={area.icon}
                      checked={values.areas.includes(area.name)}
                      onToggle={() => toggleArea(area.name)}
                    />
                  ))}
                </div>
              </SectionCard>

              <SectionCard title="5. Consent" tone="lavender" icon={FileText}>
                <div className={styles.consentList}>
                  <ConsentCheck
                    id="consentContact"
                    checked={values.consentContact}
                    error={errors.consentContact}
                    onChange={(checked) => updateField('consentContact', checked)}
                    label="I give permission to be contacted regarding the I-GROW programme, upcoming batches, events and related information."
                  />
                  <ConsentCheck
                    id="consentAccuracy"
                    checked={values.consentAccuracy}
                    error={errors.consentAccuracy}
                    onChange={(checked) => updateField('consentAccuracy', checked)}
                    label="I confirm that the information provided is accurate to the best of my knowledge."
                  />
                </div>
              </SectionCard>

              {submitError ? (
                <p className={styles.formError} role="alert">
                  {submitError}
                </p>
              ) : null}

              <div className={styles.submitWrap}>
                <button className={styles.submit} type="submit" disabled={isSubmitting} aria-busy={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit Registration'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </main>
  );
}

type ConsentCheckProps = {
  id: 'consentContact' | 'consentAccuracy';
  label: string;
  checked: boolean;
  error?: string;
  onChange: (checked: boolean) => void;
};

function ConsentCheck({ id, label, checked, error, onChange }: ConsentCheckProps) {
  const errorId = `${id}-error`;

  return (
    <div className={styles.field}>
      <label className={styles.consent} htmlFor={id}>
        <input
          id={id}
          className={styles.visuallyHidden}
          type="checkbox"
          name={id}
          checked={checked}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span className={styles.tick} aria-hidden="true">
          <svg className={styles.check} viewBox="0 0 12 12" fill="none">
            <path d="M2.2 6.2 4.7 8.7 9.8 3.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <span>
          {label}{' '}
          <span className={styles.asterisk} aria-hidden="true">
            *
          </span>
          <span className={styles.visuallyHidden}> required</span>
        </span>
      </label>
      {error ? (
        <p className={styles.error} id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
